import React, { useState, useCallback, useEffect } from 'react';
import {
  Card, CardContent, Typography, TextField, Box, List, ListItem,
  ListItemAvatar, Avatar, ListItemText, Chip, CircularProgress, IconButton,
  InputAdornment
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';
import BarcodeScanner from './BarcodeScanner';

const RECENT_KEY = 'recentFoodSearches';
const MAX_RECENT = 5;

interface FoodProduct {
  name: string;
  brand: string;
  kcal_per_100g: number;
  protein_per_100g: number;
  carbs_per_100g: number;
  fat_per_100g: number;
  image_url: string;
  serving_size: string;
  barcode: string;
}

function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveRecent(q: string) {
  const prev = loadRecent();
  const updated = [q, ...prev.filter(s => s !== q)].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
}

const FoodSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoodProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [focused, setFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(loadRecent);
  const { t } = useLanguage();

  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  const abortRef = React.useRef<AbortController | null>(null);
  const blurTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(blurTimeoutRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setSearched(true);
    try {
      const response = await api.get('/food/search', { params: { q }, signal: controller.signal });
      setResults(response.data.products || []);
      saveRecent(q.trim());
      setRecentSearches(loadRecent());
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name;
      if (name !== 'CanceledError' && name !== 'AbortError') {
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 500);
  };

  const handleRecentClick = (q: string) => {
    setQuery(q);
    doSearch(q);
  };

  const handleProductScanned = (product: FoodProduct) => {
    setScanning(false);
    setResults([product]);
    setSearched(true);
    setQuery(product.name || '');
  };

  const showRecent = focused && query.length === 0 && recentSearches.length > 0;

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t.searchFood}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t.searchFoodBrowseNote}
        </Typography>

        {scanning ? (
          <BarcodeScanner
            onProductScanned={handleProductScanned}
            onClose={() => setScanning(false)}
          />
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder={t.searchFoodProducts}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => { blurTimeoutRef.current = setTimeout(() => setFocused(false), 150); }}
              sx={{ mb: showRecent ? 1 : 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setScanning(true)}
                      edge="end"
                      title={t.scanBarcode}
                      size="small"
                    >
                      <CameraAltIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {showRecent && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{t.recentSearches}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {recentSearches.map(s => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      variant="outlined"
                      onClick={() => handleRecentClick(s)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            <List dense>
              {results.map((product, index) => (
                <ListItem key={product.barcode || index} sx={{ borderRadius: 2, mb: 0.5, bgcolor: 'background.default' }}>
                  <ListItemAvatar>
                    {product.image_url ? (
                      <Avatar src={product.image_url} variant="rounded" />
                    ) : (
                      <Avatar variant="rounded">?</Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {product.name || 'Unknown product'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {product.brand && (
                          <Typography variant="caption" color="text.secondary">
                            {product.brand}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={`${Math.round(product.kcal_per_100g)} ${t.kcal}`} size="small" color="primary" variant="outlined" />
                          <Chip label={`P: ${Math.round(product.protein_per_100g)}g`} size="small" variant="outlined" />
                          <Chip label={`C: ${Math.round(product.carbs_per_100g)}g`} size="small" variant="outlined" />
                          <Chip label={`F: ${Math.round(product.fat_per_100g)}g`} size="small" variant="outlined" />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {t.per100g}
                          {product.serving_size ? ` | ${t.serving}: ${product.serving_size}` : ''}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {searched && !loading && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {t.noProductsFound}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FoodSearch;
